export type RevoteConfirmKind = 'earlyBonus' | 'final' | 'finalWithEarlyBonus';

export type RevoteUiFlag =
  | 'revote.confirm.earlyBonus'
  | 'revote.confirm.final'
  | 'revote.confirm.finalWithEarlyBonus';

export type RevoteBonusDelta = 'same' | 'downgrade' | 'lost';

export type RevoteConfirmBonus = {
  from: number;
  to: number;
  /** Prefer API `early_bonus_window` (derived from rating config hours). */
  window?: string;
};

export const REVOTE_CONFIRM_UI_FLAG_BY_KIND: Record<RevoteConfirmKind, RevoteUiFlag> = {
  earlyBonus: 'revote.confirm.earlyBonus',
  final: 'revote.confirm.final',
  finalWithEarlyBonus: 'revote.confirm.finalWithEarlyBonus',
};

const FINAL_BODY =
  'This is your final change for this duel - after this you won\'t be able to switch back.';

const FINAL_BODY_PREFIX =
  'This is your final change for this duel - you won\'t be able to switch back.';

/** Format multiplier for UI (matches GameweekHeader ×N.NN style). */
export function formatEarlyBonusMultiplier(value: number): string {
  return `×${value.toFixed(2)}`;
}

/**
 * Rough window label for the *new* vote tier (production copy).
 * Thresholds mirror server rating brackets (5d / 3d / 1d / late).
 */
export function earlyBonusWindowLabel(multiplier: number): string {
  if (multiplier >= 1.15) return '5+ days before deadline';
  if (multiplier >= 1.1) return '3-5 days before deadline';
  if (multiplier >= 1.05) return '1-3 days before deadline';
  return 'less than 1 day before deadline';
}

export function resolveRevoteBonusDelta(from: number, to: number): RevoteBonusDelta {
  if (to >= from) return 'same';
  if (to <= 1) return 'lost';
  return 'downgrade';
}

export function buildEarlyBonusLine(bonus: RevoteConfirmBonus): string {
  const from = formatEarlyBonusMultiplier(bonus.from);
  const to = formatEarlyBonusMultiplier(bonus.to);
  const window = bonus.window ?? earlyBonusWindowLabel(bonus.to);
  const delta = resolveRevoteBonusDelta(bonus.from, bonus.to);

  if (delta === 'same') {
    return `Your Early Bonus stays at ${to} (${window}).`;
  }
  if (delta === 'downgrade') {
    return `Your Early Bonus drops from ${from} to ${to} (${window}).`;
  }
  return `Your Early Bonus is lost: ${from} → ${to} (${window}).`;
}

export function buildRevoteConfirmCopy(
  kind: RevoteConfirmKind,
  bonus?: RevoteConfirmBonus | null,
): { title: string; body: string } {
  if (kind === 'final') {
    return { title: 'Last change', body: FINAL_BODY };
  }

  const bonusLine =
    bonus != null
      ? buildEarlyBonusLine(bonus)
      : 'Your Early Bonus will drop if you change your pick now.';

  if (kind === 'earlyBonus') {
    return {
      title: 'Change your pick?',
      body: bonusLine,
    };
  }

  return {
    title: 'Last change',
    body: `${FINAL_BODY_PREFIX} ${bonusLine}`,
  };
}

const REVOTE_FLAG_LOCAL_STORAGE_PREFIX = 'crowdfpl_ui_flag:';

export function readRevoteFlagFromStorage(flag: RevoteUiFlag): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(`${REVOTE_FLAG_LOCAL_STORAGE_PREFIX}${flag}`) === '1';
}

export function writeRevoteFlagToStorage(flag: RevoteUiFlag): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(`${REVOTE_FLAG_LOCAL_STORAGE_PREFIX}${flag}`, '1');
}
